import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { UscgMapContainer } from 'components/MapPage/UscgMapContainer';
import SitrepDocumentSection from 'components/Reports/document/SitrepDocumentSection';
import {
  nestSitrepSections,
  type NestedSitrepSection,
} from 'components/Reports/document/nestSitrepSections';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { PresentedReportFrame } from 'components/IAP/presentation/presentedReportScope';

type ReportSlidePaneProps = {
  sitrep: GetSitrepsResponse;
  section: NestedSitrepSection | undefined;
  canEdit?: boolean;
  showAuthoringActions?: boolean;
  sectionAILocks?: Map<string, unknown>;
  isGenerating?: boolean;
  onRefetch?: () => void;
  onOpenSettings?: (section: SitrepSectionType) => void;
  onDeleteSection?: (section: SitrepSectionType) => void;
  onAIGenerate?: (section: SitrepSectionType) => void;
  enableLiveAnalyticsRefresh?: boolean;
};

/** One report section on the left and the live map on the right. */
export function ReportSlidePane({
  sitrep,
  section,
  canEdit = false,
  showAuthoringActions = false,
  sectionAILocks,
  isGenerating = false,
  onRefetch,
  onOpenSettings,
  onDeleteSection,
  onAIGenerate,
  enableLiveAnalyticsRefresh = false,
}: ReportSlidePaneProps) {
  if (!section) {
    return (
      <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
        This report has no sections.
      </p>
    );
  }
  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-1">
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:p-4">
        <SitrepDocumentSection
          section={section}
          sitrepId={sitrep.id}
          sitrep={sitrep}
          sitrepWorkspaceId={sitrep.incident_id}
          sitrepTemplateId={sitrep.sitrep_template_id}
          canEdit={canEdit}
          showAuthoringActions={showAuthoringActions}
          isLocked={!!sectionAILocks?.get(section.id)}
          isGenerating={isGenerating}
          childSections={section.children}
          sectionAILocks={sectionAILocks}
          onUpdated={onRefetch ?? (() => undefined)}
          onOpenSettings={onOpenSettings}
          onDelete={onDeleteSection}
          onAIGenerate={onAIGenerate}
          enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
        />
      </div>
      <div className="tw:min-h-0 tw:w-1/2 tw:border-l tw:border-border">
        <UscgMapContainer />
      </div>
    </div>
  );
}

type ReportPresentationLayout = 'document' | 'quad';

const QUAD_SLIDE_SIZE = 4;

type ReportPresentationProps = {
  sitrep: GetSitrepsResponse;
  sections: SitrepSectionType[];
  canEdit: boolean;
  showAuthoringActions?: boolean;
  sectionAILocks?: Map<string, unknown>;
  isGenerating?: boolean;
  onRefetch: () => void;
  onOpenSettings?: (section: SitrepSectionType) => void;
  onDeleteSection?: (section: SitrepSectionType) => void;
  onAIGenerate?: (section: SitrepSectionType) => void;
  onExit: () => void;
  enableLiveAnalyticsRefresh?: boolean;
  /** Document is one section plus the map. Quad is four widgets per slide. */
  layout?: ReportPresentationLayout;
};

function chunkSections(
  sections: NestedSitrepSection[],
  size: number
): NestedSitrepSection[][] {
  const slides: NestedSitrepSection[][] = [];
  for (let index = 0; index < sections.length; index += size) {
    slides.push(sections.slice(index, index + size));
  }
  return slides;
}

function QuadSlide({
  sitrep,
  sections,
  canEdit = false,
  showAuthoringActions = false,
  sectionAILocks,
  isGenerating = false,
  onRefetch,
  onOpenSettings,
  onDeleteSection,
  onAIGenerate,
  enableLiveAnalyticsRefresh = false,
}: Omit<ReportSlidePaneProps, 'section'> & {
  sections: NestedSitrepSection[];
}) {
  if (sections.length === 0) {
    return (
      <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
        This report has no sections.
      </p>
    );
  }
  const cells = Array.from(
    { length: QUAD_SLIDE_SIZE },
    (_, index) => sections[index]
  );
  return (
    <div className="tw:grid tw:h-full tw:min-h-0 tw:flex-1 tw:grid-cols-2 tw:grid-rows-2 tw:gap-2 tw:overflow-hidden tw:p-2">
      {cells.map((section, index) => (
        <div
          key={section?.id ?? `empty-${index}`}
          className="tw:min-h-0 tw:overflow-auto"
        >
          {section ? (
            <SitrepDocumentSection
              section={section}
              sitrepId={sitrep.id}
              sitrep={sitrep}
              sitrepWorkspaceId={sitrep.incident_id}
              sitrepTemplateId={sitrep.sitrep_template_id}
              canEdit={canEdit}
              showAuthoringActions={showAuthoringActions}
              isLocked={!!sectionAILocks?.get(section.id)}
              isGenerating={isGenerating}
              childSections={section.children}
              sectionAILocks={sectionAILocks}
              onUpdated={onRefetch ?? (() => undefined)}
              onOpenSettings={onOpenSettings}
              onDelete={onDeleteSection}
              onAIGenerate={onAIGenerate}
              enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Fullscreen deck of a report. Document layout puts one top-level section on
 * each slide beside the live map. Quad layout puts four section widgets on each
 * slide in a 2x2 grid.
 */
export function ReportPresentation({
  sitrep,
  sections,
  canEdit,
  showAuthoringActions = false,
  sectionAILocks,
  isGenerating = false,
  onRefetch,
  onOpenSettings,
  onDeleteSection,
  onAIGenerate,
  onExit,
  enableLiveAnalyticsRefresh = false,
  layout = 'document',
}: ReportPresentationProps) {
  const nested = useMemo(() => nestSitrepSections(sections), [sections]);
  const documentSlides = nested;
  const quadSlides = useMemo(
    () => chunkSections(nested, QUAD_SLIDE_SIZE),
    [nested]
  );
  const slideCount =
    layout === 'quad' ? quadSlides.length : documentSlides.length;
  const [index, setIndex] = useState(0);
  const safeIndex = slideCount === 0 ? 0 : Math.min(index, slideCount - 1);
  const documentSlide = documentSlides[safeIndex];
  const quadSlide = quadSlides[safeIndex] ?? [];

  return createPortal(
    <div className="dark tw:fixed tw:inset-0 tw:z-50 tw:flex tw:flex-col tw:bg-background tw:text-foreground">
      <div className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2">
        <Button type="button" variant="outline" size="sm" onClick={onExit}>
          Exit
        </Button>
        {slideCount > 0 ? (
          <span className="tw:text-sm tw:text-muted-foreground">
            {safeIndex + 1} / {slideCount}
          </span>
        ) : null}
        <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={slideCount === 0 || safeIndex === 0}
            onClick={() => setIndex(safeIndex - 1)}
          >
            <ChevronLeft className="tw:size-4" />
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={slideCount === 0 || safeIndex >= slideCount - 1}
            onClick={() => setIndex(safeIndex + 1)}
          >
            Next
            <ChevronRight className="tw:size-4" />
          </Button>
        </div>
      </div>
      <PresentedReportFrame sitrep={sitrep}>
        {layout === 'quad' ? (
          <QuadSlide
            sitrep={sitrep}
            sections={quadSlide}
            canEdit={canEdit}
            showAuthoringActions={showAuthoringActions}
            sectionAILocks={sectionAILocks}
            isGenerating={isGenerating}
            onRefetch={onRefetch}
            onOpenSettings={onOpenSettings}
            onDeleteSection={onDeleteSection}
            onAIGenerate={onAIGenerate}
            enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
          />
        ) : (
          <ReportSlidePane
            sitrep={sitrep}
            section={documentSlide}
            canEdit={canEdit}
            showAuthoringActions={showAuthoringActions}
            sectionAILocks={sectionAILocks}
            isGenerating={isGenerating}
            onRefetch={onRefetch}
            onOpenSettings={onOpenSettings}
            onDeleteSection={onDeleteSection}
            onAIGenerate={onAIGenerate}
            enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
          />
        )}
      </PresentedReportFrame>
    </div>,
    document.body
  );
}
